import { Button, Card, ResizableLayout, Toaster } from "@dune/ui";
import "@dune/ui/globals.css";
import { S3File } from "@dune/types";
import { ArrowLeft, DownloadCloud01, XClose } from "@untitled-ui/icons-react";
import { useState } from "react";
import {
	Control,
	FieldErrors,
	UseFormHandleSubmit,
	UseFormSetValue,
} from "react-hook-form";
import { FileViewer } from "../../../component/common/FileViewer";
import { ControlledS3FileInput } from "../../../component/form/controlledInput";
import { RegisterSecondStepDetailsFormData } from "./UseRegisterSecondStepBroker";
import { RegisterSecondStepFilesFormData } from "./useRegisterFilesForSecondStepForm";

export function shouldShowGuarantee(
	categories?: RegisterSecondStepDetailsFormData["categories"],
) {
	if (!categories || categories.length === 0) return false;

	const coaCategory = categories.find((cat) => cat.category === "COA");
	if (coaCategory) {
		return coaCategory.fundCollection === true;
	}

	const goaCategory = categories.find((cat) => cat.category === "GOA");
	if (goaCategory) {
		return goaCategory.fundCollection === true;
	}

	return false;
}

interface CompleteFilesProps {
	backStep: () => void;
	onFinalize: (files: RegisterSecondStepFilesFormData) => Promise<void>;
	categoriesForFileForm?: RegisterSecondStepDetailsFormData["categories"];
	filesForm: RegisterSecondStepFilesFormData;
	control: Control<RegisterSecondStepFilesFormData>;
	errors: FieldErrors<RegisterSecondStepFilesFormData>;
	isLoading: boolean;
	handleSubmit: UseFormHandleSubmit<RegisterSecondStepFilesFormData>;
	setValue: UseFormSetValue<RegisterSecondStepFilesFormData>;
}

// Fichier par défaut pour les réinitialisations
const defaultFile: S3File & { size: number } = {
	size: 0,
	name: "",
	bucket: "private",
	key: "",
	type: "",
	url: "",
};

export default function CompleteFiles({
	backStep,
	onFinalize,
	categoriesForFileForm,
	control,
	errors,
	isLoading,
	handleSubmit,
	filesForm,
	setValue,
}: CompleteFilesProps) {
	const [previewFile, setPreviewFile] = useState<S3File | null>(null);
	const isFormValid = Object.keys(errors).length === 0;

	const isDefaultFile = (file: S3File | undefined | null) => {
		if (!file) return true;
		return (
			file.size === 0 && file.name === "" && file.key === "" && file.type === ""
		);
	};

	const getFileForInput = (
		file: S3File | undefined | null,
	): S3File | undefined => {
		return isDefaultFile(file) ? undefined : file || undefined;
	};

	const removeKbis = () => setValue("kbis", { ...defaultFile });
	const removeIdentity = () => setValue("identity", { ...defaultFile });
	const removeRib = () => setValue("rib", { ...defaultFile });
	const removeRc = () => setValue("rc", { ...defaultFile });
	const removeGuarantee = () => setValue("guarantee", { ...defaultFile });

	const onSubmit = async (data: RegisterSecondStepFilesFormData) => {
		try {
			await onFinalize(data);
		} catch (error) {
			console.error("Erreur lors de la soumission des fichiers :", error);
		}
	};

	const handlePreview = (file: S3File) => {
		if (previewFile?.key === file.key) {
			setPreviewFile(null);
		} else {
			setPreviewFile(file);
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<div className="text-gray-600">Chargement des documents...</div>
			</div>
		);
	}

	return (
		<ResizableLayout className="h-full">
			<ResizableLayout.Main>
				<div>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							handleSubmit(onSubmit)();
						}}
						className="max-w-[950px] w-full mx-auto bg-white p-8"
					>
						<div className="flex-1 p-6 space-y-6">
							<div className="flex flex-col gap-1">
								<div className="text-2xl text-text-main-blue">
									Bienvenue sur la plateforme DUNE
								</div>
								<div className="text-xl font-semibold mb-7">
									Ajoutez vos documents
								</div>
								<Card className="p-6 flex flex-col gap-4 overflow-auto text-center">
									<div className="flex flex-row flex-wrap  gap-6">
										<div className="flex flex-row gap-6 w-full">
											<ControlledS3FileInput
												name="kbis"
												control={control}
												bucket="private"
												label="KBIS"
												onPreview={handlePreview}
												initialFile={getFileForInput(filesForm.kbis)}
												onRemove={removeKbis}
											/>
											<ControlledS3FileInput
												name="identity"
												control={control}
												bucket="private"
												label="Pièce d'identité du gérant"
												onPreview={handlePreview}
												initialFile={getFileForInput(filesForm.identity)}
												onRemove={removeIdentity}
											/>
										</div>
										<div className="flex flex-row gap-6 w-full">
											<ControlledS3FileInput
												name="rib"
												control={control}
												bucket="private"
												label="RIB"
												onPreview={handlePreview}
												initialFile={getFileForInput(filesForm.rib)}
												onRemove={removeRib}
											/>
											<ControlledS3FileInput
												name="rc"
												control={control}
												bucket="private"
												label="Attestation de RC Professionnelle"
												onPreview={handlePreview}
												initialFile={getFileForInput(filesForm.rc)}
												onRemove={removeRc}
											/>
										</div>
										<div className="w-1/2 space-y-2">
											{shouldShowGuarantee(categoriesForFileForm) && (
												<ControlledS3FileInput
													name="guarantee"
													control={control}
													bucket="private"
													label="Attestation de Garantie Financière"
													onPreview={handlePreview}
													initialFile={getFileForInput(filesForm.guarantee)}
													onRemove={removeGuarantee}
												/>
											)}
										</div>
									</div>
								</Card>
							</div>

							<div className="flex justify-between">
								<Button
									variant="outline"
									type="button"
									className="h-11"
									onClick={backStep}
								>
									<ArrowLeft /> Précédent
								</Button>
								<Button type="submit" className="h-11" disabled={!isFormValid}>
									Envoyer ma demande
								</Button>
							</div>
						</div>
					</form>
					<Toaster />
				</div>
			</ResizableLayout.Main>

			{previewFile && (
				<ResizableLayout.Panel>
					<ResizableLayout.PanelHeader>
						<ResizableLayout.PanelTitle>
							{previewFile.name}
						</ResizableLayout.PanelTitle>
						<Button
							variant="outline"
							onClick={async () => {
								if (!previewFile?.url || !previewFile?.name) return;

								try {
									const response = await fetch(previewFile.url);
									const blob = await response.blob();
									const blobUrl = URL.createObjectURL(blob);

									const link = document.createElement("a");
									link.href = blobUrl;
									link.download = previewFile.name;
									document.body.appendChild(link);
									link.click();
									document.body.removeChild(link);
									URL.revokeObjectURL(blobUrl);
								} catch (err) {
									console.error("Erreur lors du téléchargement :", err);
								}
							}}
						>
							<DownloadCloud01 className="text-background-blue" />
							Télécharger
						</Button>
						<Button
							variant="ghost"
							onClick={() => {
								setPreviewFile(null);
							}}
						>
							<XClose />
						</Button>
					</ResizableLayout.PanelHeader>
					<ResizableLayout.PanelContent>
						<FileViewer
							file={previewFile.url || ""}
							type={previewFile.type}
							className="w-full h-svh"
						/>
					</ResizableLayout.PanelContent>
				</ResizableLayout.Panel>
			)}
		</ResizableLayout>
	);
}
